<template>
    <transition>
      <div class="container-fluid page-content">
        <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center page-header-row gap-3">
            <div>
                <h4 class="page-title text-slate-800 fw-bold mb-1">Products</h4>
                <nav aria-label="breadcrumb">
                    <ol class="breadcrumb mb-0">
                        <li class="breadcrumb-item"><a href="/" class="text-muted"><i class="fas fa-home me-1"></i>Home</a></li>
                        <li class="breadcrumb-item"><a href="javascript:void(0);" class="text-muted">Products</a></li>
                        <li class="breadcrumb-item active text-indigo fw-medium" aria-current="page">Product List</li>
                    </ol>
                </nav>
            </div>
            <div class="d-flex flex-wrap justify-content-start justify-content-sm-end gap-2">
                <button type="button" class="btn btn-outline-premium" v-if="permission['product-barcode']" @click="toggleBarcode">
                    <i class="fas fa-barcode me-2"></i>Barcode
                </button>
                <button type="button" class="btn btn-outline-premium" v-if="permission['product-bulk-import']" @click="toggleImportModal">
                    <i class="fas fa-upload me-2"></i>Quick Products Upload
                </button>
                <button type="button" class="btn btn-outline-success-premium" v-if="permission['product-bulk-import']" @click="masterDataUploadModal">
                    <i class="fas fa-database me-2"></i>Master Data Upload
                </button>
                <button type="button" v-if="permission['product-create']" @click="toggleModal" class="btn btn-premium d-flex align-items-center gap-2">
                    <i class="fas fa-plus"></i> Add Products
                </button>
            </div>
        </div>

        <div class="card border-0 shadow-sm filter-card">
            <div class="card-body p-3">
                <div class="row align-items-end g-3">
                    <div class="col-lg-3 col-md-4 col-sm-6">
                        <label class="form-label-custom text-muted mb-2"><i class="fas fa-building me-1"></i>Company</label>
                        <select class="form-select-custom" v-model="filter.company_id" id="company_filter" @change="filterData()"> 
                            <option value="">--- All Companies ---</option>
                            <option v-for="(company, index) in companies" :value="company.id" :key="index">{{ company.name }}</option>
                        </select> 
                    </div>
                    <div class="col-lg-3 col-md-4 col-sm-6">
                        <label class="form-label-custom text-muted mb-2"><i class="fas fa-layer-group me-1"></i>Category</label>
                        <Multiselect 
                            class="form-control-custom-select" 
                            mode="single"
                            v-model="filter.category_id"
                            placeholder="Category"  
                            @change="switchSelect($event)" 
                            :searchable="true" 
                            :filter-results="true"
                            :options="parents"
                            :classes="multiclasses"
                            :close-on-select="true" 
                            :min-chars="1"
                            :resolve-on-load="false" 
                            :create-option="true" 
                        />   
                    </div> 
                    <div class="col-lg-3 col-md-4 col-sm-6">
                        <label class="form-label-custom text-muted mb-2"><i class="fas fa-sitemap me-1"></i>Sub Category</label>
                        <Multiselect 
                            class="form-control-custom-select" 
                            mode="single"
                            v-model="filter.sub_cat_id"
                            placeholder="Choose Sub Category"  
                            @change="filterData()" 
                            :searchable="true" 
                            :filter-results="true"
                            :options="childs"
                            :classes="multiclasses"
                            :close-on-select="true" 
                            :min-chars="1"
                            :resolve-on-load="false" 
                            :create-option="true" 
                        />    
                    </div> 
                    <div class="col-lg-3 col-md-4 col-sm-6">
                        <label class="form-label-custom text-muted mb-2"><i class="fas fa-tag me-1"></i>Brand</label>
                        <select class="form-select-custom" v-model="filter.brand_id" id="brand" @change="filterData()"> 
                            <option value="">Choose Brand</option>
                            <option v-for="(brand, index) in brands" :value="brand.id" :key="index">{{ brand.name }}</option>
                        </select> 
                    </div>
                </div> 
            </div>
        </div>

        <div class="card border-0 shadow-sm filter-card">
            <div class="card-body p-3">
                <div class="row align-items-center g-2">
                    <div class="col-md-6">
                        <div class="d-flex align-items-center gap-2">
                            <span class="text-muted text-nowrap">Show</span>
                            <select class="form-select-custom length-select" v-model="tableData.length" @change="fetchItems()">   
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                            <span class="text-muted text-nowrap">entries</span>
                        </div>
                    </div>
                    <div class="col-md-6 d-flex justify-content-md-end">
                        <div class="position-relative search-container">
                            <i class="fas fa-search search-icon text-muted"></i>
                            <input type="text" class="form-control-custom search-input" v-model="tableData.search" placeholder="Search products..." @input="fetchItems()">
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="card border-0 shadow-sm table-card">
            <div class="card-body p-0">
                <div class="table-responsive-custom">
                    <table class="table-premium mb-0" v-if="!loading && items.length > 0">
                        <thead>
                            <tr>
                                <th class="text-start" @click="sortBy('company', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 12%; cursor: pointer;">
                                    Company
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'company' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'company' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-start" @click="sortBy('product_name', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 20%; cursor: pointer;">
                                    Product Name
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'product_name' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'product_name' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-start" @click="sortBy('product_native_name', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 18%; cursor: pointer;">
                                    Native Name
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'product_native_name' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'product_native_name' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-end" @click="sortBy('cost_price', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 10%; cursor: pointer;">
                                    Cost Price
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'cost_price' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'cost_price' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-end" @click="sortBy('mrp_price', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 10%; cursor: pointer;">
                                    MRP Price
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'mrp_price' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'mrp_price' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-start" @click="sortBy('product_code', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 12%; cursor: pointer;">
                                    Product Code
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'product_code' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'product_code' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-start" @click="sortBy('category.name', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 12%; cursor: pointer;">
                                    Category
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'category.name' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'category.name' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-center" @click="sortBy('alert_quantity', tableData.dir === 'asc' ? 'desc' : 'asc')" style="width: 8%; cursor: pointer;">
                                    Alert Qty
                                    <i class="fas fa-sort ms-1 text-muted" :class="{'fa-sort-up text-indigo': tableData.sortKey === 'alert_quantity' && tableData.dir === 'asc', 'fa-sort-down text-indigo': tableData.sortKey === 'alert_quantity' && tableData.dir === 'desc'}"></i>
                                </th>
                                <th class="text-center" style="width: 6%;">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, i) in items" :key="item.id || i">
                                <td class="text-start text-muted">{{ item.company ? item.company.name : '-' }}</td>
                                <td class="text-start"><span class="fw-bold text-slate-800">{{ item.product_name }}</span></td>
                                <td class="text-start text-muted">{{ item.product_native_name || '-' }}</td>
                                <td class="text-end">{{ item.cost_price }}</td>
                                <td class="text-end fw-semibold">{{ item.mrp_price }}</td>
                                <td class="text-start"><span class="badge badge-code">{{ item.product_code }}</span></td>
                                <td class="text-start text-muted">{{ item.category ? item.category.name : '-' }}</td>
                                <td class="text-center">{{ item.alert_quantity }}</td>
                                <td class="text-center">
                                    <div class="dropdown">
                                        <button class="btn btn-icon" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                                            <i class="fas fa-ellipsis-v"></i>
                                        </button>
                                        <ul class="dropdown-menu dropdown-menu-end border-0 shadow-lg dropdown-premium">
                                            <li>
                                                <a href="javascript:void(0);" v-if="permission['product-barcode']" @click="singleBarcode(item)" class="dropdown-item text-info"><i class="fa-solid fa-barcode me-2"></i>Print Barcode</a>
                                            </li>
                                            <li>
                                                <a href="javascript:void(0);" v-if="permission['product-show']" @click="show(item)" class="dropdown-item text-primary"><i class="fa-solid fa-eye me-2"></i>View</a>
                                            </li>
                                            <li>
                                                <a href="javascript:void(0);" class="dropdown-item text-warning" v-if="permission['product-edit']" @click="edit(item)"><i class="fas fa-edit me-2"></i>Edit</a>
                                            </li>
                                            <li><hr class="dropdown-divider"></li>
                                            <li>
                                                <a href="javascript:void(0);" class="dropdown-item text-danger" v-if="permission['product-delete']" @click="deleteItem(item)"><i class="fas fa-trash-alt me-2"></i>Remove</a>
                                            </li>
                                        </ul>
                                    </div>   
                                </td>
                            </tr> 
                        </tbody>
                    </table>

                    <div v-if="!loading && items.length === 0" class="text-center py-5">
                        <img src="../../assets/image/loading.gif" class="mb-3 opacity-25" style="width: 80px; filter: grayscale(1);" />
                        <h5 class="text-muted fw-semibold">No Products Found</h5>
                        <p class="text-muted mb-0 small">Try adjusting your filters or search keywords.</p>
                    </div>

                    <div v-if="loading" class="text-center py-5">
                        <div class="spinner-premium mb-3"></div>
                        <h5 class="text-muted fw-semibold">Loading Products</h5>
                        <p class="text-muted mb-0 small">Please wait a moment...</p>
                    </div>
                </div>

                <div class="card-footer border-0 bg-white py-3 px-4 d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3" v-if="pagination && items.length > 0">
                    <div class="text-muted small">
                        Showing <span class="fw-semibold text-slate-800">{{ pagination.from }}</span> to <span class="fw-semibold text-slate-800">{{ pagination.to }}</span> of <span class="fw-semibold text-slate-800">{{ pagination.total }}</span> entries
                    </div>
                    <Pagination 
                        :pagination="pagination"  
                        :language="lang"
                        @onChangePage="setPage" > 
                    </Pagination>
                </div>
            </div>
        </div>
        <!-- Product Add Form -->
        <Modal @close="toggleBarcode" :modalActive="modalActiveBarcode">
            <div class="modal-content scrollbar-width-thin product-barcode-modal">
                <div class="modal-header"> 
                    <h5>Product Barcode </h5>
                    <button @click="toggleBarcode" type="button" class="btn btn-default">X</button>
                </div> 
                <div class="modal-body " id="barcodePrint">  
                    <div class="row d-print-none">
                      <div class="col-md-3">    
                          <label class="form-label" for="product_native_name">Product </label> <br> 
                          <Multiselect 
                            class="form-control border customer_id" 
                            mode="single"
                            v-model="form.customer_id"
                            placeholder="Products"  
                            @change="selectProduct($event)"
                            :searchable="true" 
                            :filter-results="true"
                            :options="products"
                            :classes="multiclasses"
                            :close-on-select="true" 
                            :min-chars="1"
                            :resolve-on-load="false" 
                          />     
                      </div>
                      <div class="col-md-2">    
                          <label class="form-label" for="product_native_name">Model</label> <br>
                          <select class="form-control border" v-model="barcode_symbology" @change="barcodeSetting"> 
                              <option value=""> Choose barcode symbology</option>
                              <option v-for="(barcode, index) in barcode_symbology_list" :value="barcode.name" :key="index" 
                                > {{barcode.name}}
                                </option>
                          </select>  
                      </div>
                      <div class="col-md-2">    
                          <label class="form-label" for="product_native_name">Colors</label> <br>
                          <select class="form-control border" v-model="color"  @change="barcodeSetting"> 
                              <option value=""> Choose Color</option>
                              <option v-for="(color, index) in colors" :value="color.label" :key="index" 
                                > {{color.label}}
                                </option>
                          </select>  
                      </div> 
                      <div class="col-md-2">  
                          <label class="form-label" for="product_native_name">Barcode Width</label> <br>  
                          <input class="form-control border" type="number" v-model="barcodeWidth" @keypress="barcodeSetting">  
                      </div>
                      <div class="col-md-2"> 
                          <label class="form-label" for="product_native_name">Barcode Height</label> <br>   
                          <input class="form-control border" type="number" v-model="barcodeHeight" @keypress="barcodeSetting">  
                      </div> 
                      <div class="col-md-2"  > 
                          <label class="form-label" >Quentity</label> <br>   
                          <input class="form-control border" type="number" v-model="barcodeQty" @keypress="barcodeSetting">  
                      </div> 
                    </div>
                    <div class="row" v-if="singleBarcodeEnable" >
                      <div class="col-md-6 col-xs-6" v-for="(item, i) in barcodeQty" > 
                        <div class="border mt-2" style="text-align: center;">
                          <span calss="barcodeName"> {{ product.product_name }}</span><br>
                          <BarcodeGenerator
                            v-if="barcodeReRender" 
                            :text="'Price '+product.mrp_price"
                            :fontSize="12"
                            :value="product.product_code"
                            :format="barcode_symbology"
                            :lineColor="color" 
                            :height="barcodeHeight"
                            :width="barcodeWidth"
                            :elementTag="'img'"
                          />  
                        </div> 
                      </div>  
                    </div>
                    <div class="row" v-if="!singleBarcodeEnable">
                      <div class="col-md-6 col-xs-6" v-for="(item, i) in items" v-if="items.length > 0"> 
                        <div class="border mt-2" style="text-align: center;">
                          <span calss="barcodeName"> {{ item.product_name }}</span><br>
                          <BarcodeGenerator
                            v-if="barcodeReRender"
                            :text="'Price '+item.mrp_price"
                            :fontSize="12" 
                            :value="item.product_code" 
                            :format="barcode_symbology"
                            :lineColor="color" 
                            :height="barcodeHeight"
                            :width="barcodeWidth"
                            :elementTag="'svg'"
                          />                            
                        </div> 
                      </div>  
                    </div>                     
                </div>
                <div class="modal-footer">
                    <button type="submit" class="btn btn-success float-right " @click="print">Print</button>
                </div> 
            </div>
        </Modal>

        <Modal @close="toggleModal" :modalActive="modalActive">
            <div class="modal-content scrollbar-width-thin modal-premium modal-premium-lg">
                <div class="modal-header"> 
                    <h4>Add New Product</h4>
                    <button @click="toggleModal" type="button" class="btn-close-custom">×</button>
                </div>
                <form @submit.prevent="submitForm()" enctype="multipart/form-data">
                    <div class="modal-body p-4"> 
                        <!-- Group 1: General Information -->
                        <div class="card bg-light-custom border-0 p-3 mb-4">
                            <h5 class="text-indigo fw-bold mb-3"><i class="fas fa-info-circle me-2"></i>General Information</h5>
                            <div class="row g-3">
                                <div class="col-md-3">
                                    <div class="form-group"> 
                                        <label class="form-label-custom" for="product_type">Product Type</label> 
                                        <select class="form-select-custom" v-model="form.product_type" id="product_type">
                                            <option value="standard">Standard</option> 
                                            <option value="combo">Combo</option>  
                                            <option value="variable">Variable</option>  
                                        </select>
                                    </div>
                                </div> 
                                <div class="col-md-3">
                                    <div class="form-group"> 
                                        <label class="form-label-custom" for="product_name">Product Name*</label>
                                        <input type="text" class="form-control-custom" :class="{'is-invalid': errors.product_name}" @keypress="onkeyPress('product_name')" v-model="form.product_name" id="product_name" placeholder="Product Name" autocomplete="off"> 
                                        <div class="invalid-feedback" v-if="errors.product_name">
                                            {{errors.product_name[0]}}
                                        </div>
                                    </div>
                                </div> 
                                <div class="col-md-3">
                                    <div class="form-group"> 
                                        <label class="form-label-custom" for="product_native_name">Product Native Name*</label>
                                        <input type="text" class="form-control-custom" :class="{'is-invalid': errors.product_native_name}" @keypress="onkeyPress('product_native_name')" v-model="form.product_native_name" id="product_native_name" placeholder="Product Native Name" autocomplete="off"> 
                                        <div class="invalid-feedback" v-if="errors.product_native_name">
                                            {{errors.product_native_name[0]}}
                                        </div>
                                    </div>
                                </div> 
                                <div class="col-md-3">
                                    <div class="form-group"> 
                                        <label class="form-label-custom" for="product_code">Product Code*</label> 
                                        <div class="d-flex align-items-center gap-2">
                                            <input type="text" class="form-control-custom" :class="{'is-invalid': errors.product_code}" @keypress="onkeyPress('product_code')" v-model="form.product_code" id="product_code" placeholder="Product Code" autocomplete="off">  
                                            <button type="button" class="btn btn-outline-premium px-3" style="height: 42px;" @click="random_num()">
                                                <i class="fa fa-random"></i>
                                            </button>
                                        </div>
                                        <div class="text-danger small mt-1" v-if="errors.product_code">
                                            {{errors.product_code[0]}}
                                        </div>
                                    </div>
                                </div>  
                            </div> 

                            <div class="row g-3 mt-1">
                                <div class="col-md-3"> 
                                    <div class="form-group">
                                        <label class="form-label-custom">Category*</label>
                                        <Multiselect 
                                            class="form-control-custom-select" 
                                            mode="single"
                                            v-model="form.category_id"
                                            placeholder="Category"  
                                            @change="switchSelect($event)" 
                                            :searchable="true" 
                                            :filter-results="true"
                                            :options="parents"
                                            :classes="multiclasses"
                                            :close-on-select="true" 
                                            :min-chars="1"
                                            :resolve-on-load="false" 
                                            :create-option="true" 
                                        />  
                                        <div class="invalid-feedback" v-if="errors.category_id">
                                            {{errors.category_id[0]}}
                                        </div>
                                    </div>
                                </div> 
                                <div class="col-md-3">
                                    <div class="form-group"> 
                                        <label class="form-label-custom">Sub Category*</label>
                                        <Multiselect 
                                            class="form-control-custom-select" 
                                            mode="single"
                                            v-model="form.sub_category_id"
                                            placeholder="Choose Sub Category"  
                                            @change="onkeyPress('sub_category_id')" 
                                            :searchable="true" 
                                            :filter-results="true"
                                            :options="childs"
                                            :classes="multiclasses"
                                            :close-on-select="true" 
                                            :min-chars="1"
                                            :resolve-on-load="false" 
                                            :create-option="true" 
                                        />  
                                        <div class="invalid-feedback" v-if="errors.sub_category_id">
                                            {{errors.sub_category_id[0]}}
                                        </div> 
                                    </div> 
                                </div> 
                                <div class="col-md-3">
                                    <div class="form-group"> 
                                        <label class="form-label-custom" for="brand_id">Brand</label> 
                                        <select class="form-select-custom" v-model="form.brand_id" id="brand_id"> 
                                            <option value="">Choose Brand</option>
                                            <option v-for="(brand, index) in brands" :value="brand.id" :key="index">{{brand.name}}</option>
                                        </select>
                                    </div> 
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group"> 
                                        <label class="form-label-custom">Supplier/Vendor</label>
                                        <Multiselect
                                            class="form-control-custom-select" 
                                            mode="tags"
                                            v-model="form.supplier_id"
                                            placeholder="Supplier/Vendor"
                                            :classes="multiclasses"
                                            :searchable="true" 
                                            :options="suppliers"  
                                            :close-on-select="true" 
                                            :create-option="true"  
                                        /> 
                                    </div> 
                                </div>  
                            </div>
                        </div>

                        <!-- Group 2: Pricing & Stock -->
                        <div class="card bg-light-custom border-0 p-3 mb-4">
                            <h5 class="text-indigo fw-bold mb-3"><i class="fas fa-dollar-sign me-2"></i>Pricing & Stock</h5>
                            <div class="row g-3">  
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="min_order_qty">Min Order Qty*</label>
                                        <input type="text" class="form-control-custom" :class="{'is-invalid': errors.min_order_qty}" @keypress="onkeyPress('min_order_qty')" v-model="form.min_order_qty" id="min_order_qty" placeholder="Ex: 12" autocomplete="off"> 
                                        <div class="invalid-feedback" v-if="errors.min_order_qty">
                                            {{errors.min_order_qty[0]}}
                                        </div>
                                    </div> 
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="cost_price">Cost Price/Qty*</label>
                                        <input type="text" class="form-control-custom" :class="{'is-invalid': errors.cost_price}" @keypress="onkeyPress('cost_price')" v-model="form.cost_price" id="cost_price" placeholder="Cost Price" autocomplete="off"> 
                                        <div class="invalid-feedback" v-if="errors.cost_price">
                                            {{errors.cost_price[0]}}
                                        </div>
                                    </div> 
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="depo_price">Depo Price</label> 
                                        <input type="text" class="form-control-custom" :class="{'is-invalid': errors.depo_price}" @keypress="validateNumber($event, 'depo_price')" v-model="form.depo_price" id="depo_price" placeholder="Depo Price" autocomplete="off"> 
                                        <div class="text-danger small mt-1" v-if="!isValid">Number is Invalid</div>
                                        <div class="invalid-feedback" v-if="errors.depo_price">
                                            {{errors.depo_price[0]}}
                                        </div>
                                    </div> 
                                </div> 
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="mrp_price">MRP Price*</label>
                                        <input type="number" class="form-control-custom" :class="{'is-invalid': errors.mrp_price}" @keypress="onkeyPress('mrp_price')" v-model="form.mrp_price" id="mrp_price" placeholder="MRP Price" autocomplete="off" pattern="^\d+(?:\.\d{1,2})?$"> 
                                        <div class="invalid-feedback" v-if="errors.mrp_price">
                                            {{errors.mrp_price[0]}}
                                        </div>
                                    </div> 
                                </div>                                                 
                            </div>

                            <div class="row g-3 mt-1">
                                <div class="col-md-2">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="discount">Discount</label>
                                        <input type="text" class="form-control-custom" v-model="form.discount" id="discount" placeholder="5 or 5%" autocomplete="off">   
                                        <div class="invalid-feedback" v-if="errors.discount">
                                            {{errors.discount[0]}}
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-2">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="tax_method">Tax Method*</label>
                                        <select class="form-select-custom" v-model="form.tax_method" id="tax_method" @change="onkeyPress('tax_method')"> 
                                            <option value="">Choose Tax Method</option>  
                                            <option :value="tax.id" v-for="(tax, i) in producttaxs" :key="i" >{{tax.name}}</option>  
                                        </select>  
                                        <div class="invalid-feedback" v-if="errors.tax_method">
                                            {{errors.tax_method[0]}}
                                        </div>
                                    </div>
                                </div> 
                                <div class="col-md-2">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="product_tax">Product Tax*</label>
                                        <select class="form-select-custom" v-model="form.product_tax" id="product_tax" @change="onkeyPress('product_tax')">
                                            <option value="">Choose Tax</option>  
                                            <option :value="tax.id" v-for="(tax, i) in producttax" :key="i" >{{tax.title}}</option>  
                                        </select>  
                                        <div class="invalid-feedback" v-if="errors.product_tax">
                                            {{errors.product_tax[0]}}
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="alert_quantity">Alert Quantity*</label>
                                        <input type="text" class="form-control-custom" :class="{'is-invalid': errors.alert_quantity}" @keypress="onkeyPress('alert_quantity')" v-model="form.alert_quantity" id="alert_quantity" placeholder="Alert Qty" autocomplete="off"> 
                                        <div class="invalid-feedback" v-if="errors.alert_quantity">
                                            {{errors.alert_quantity[0]}}
                                        </div>
                                    </div> 
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom">Product Tags</label>
                                        <Multiselect
                                            class="form-control-custom-select" 
                                            mode="tags"
                                            v-model="form.product_tags"
                                            placeholder="Search or add tag"
                                            :searchable="true"
                                            :createTag="true"
                                            :options="product_tags"  
                                            :close-on-select="true" 
                                            :create-option="true"  
                                            :taggable="true"
                                        /> 
                                    </div>
                                </div>    
                            </div> 
                        </div>

                        <!-- Group 3: Units & Logistics -->
                        <div class="card bg-light-custom border-0 p-3 mb-4">
                            <h5 class="text-indigo fw-bold mb-3"><i class="fas fa-boxes me-2"></i>Units & Logistics</h5>
                            <div class="row g-3">
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="purchase_measuring_unit">Purchase Unit*</label> 
                                        <select class="form-select-custom" :class="{'is-invalid': errors.purchase_measuring_unit}" v-model="form.purchase_measuring_unit" id="purchase_measuring_unit"> 
                                            <option value="">Choose Unit</option>
                                            <option :value="unit.id" v-for="(unit, i) in units" :key="i" >{{unit.unit_name}}</option>  
                                        </select>
                                        <div class="invalid-feedback" v-if="errors.purchase_measuring_unit">
                                            {{errors.purchase_measuring_unit[0]}}
                                        </div>
                                    </div>
                                </div> 
                                <div class="col-md-2">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="sales_measuring_unit">Sales Unit*</label> 
                                        <select class="form-select-custom" :class="{'is-invalid': errors.sales_measuring_unit}" v-model="form.sales_measuring_unit" id="sales_measuring_unit">
                                            <option value="">Choose Unit</option>
                                            <option :value="unit.id" v-for="(unit, i) in units" :key="i" >{{unit.unit_name}}</option> 
                                        </select>
                                        <div class="invalid-feedback" v-if="errors.sales_measuring_unit">
                                            {{errors.sales_measuring_unit[0]}}
                                        </div>
                                    </div>
                                </div> 
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="convertion_rate">1 Purchase Unit = ? Sales Unit*</label> 
                                        <input type="number" class="form-control-custom" :class="{'is-invalid': errors.convertion_rate}" @keypress="onkeyPress('convertion_rate')" v-model="form.convertion_rate" id="convertion_rate" placeholder="Conversion Rate" autocomplete="off">  
                                        <div class="invalid-feedback" v-if="errors.convertion_rate">
                                            {{errors.convertion_rate[0]}}
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-2">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="carton_size">Carton Size</label> 
                                        <input type="number" class="form-control-custom" @keypress="onkeyPress('carton_size')" v-model="form.carton_size" id="carton_size" placeholder="Size" autocomplete="off">  
                                    </div>
                                </div>
                                <div class="col-md-2">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="carton_c_p_u">Carton CPU</label> 
                                        <input type="number" class="form-control-custom" @keypress="onkeyPress('carton_c_p_u')" v-model="form.carton_c_p_u" id="carton_c_p_u" placeholder="CPU" autocomplete="off"> 
                                    </div>
                                </div>
                            </div>

                            <div class="row g-3 mt-1">
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="is_expirable">Expirable Product</label>
                                        <select class="form-select-custom" v-model="form.is_expirable" id="is_expirable">
                                            <option :value="1">Yes</option>
                                            <option :value="0">No</option>  
                                        </select>   
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="is_ecommerce">For Web/Ecommerce</label>
                                        <select class="form-select-custom" v-model="form.is_ecommerce" id="is_ecommerce">
                                            <option :value="1">Yes</option>
                                            <option :value="0">No</option>  
                                        </select>   
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom">Vendor Define Barcode</label>
                                        <Multiselect
                                            class="form-control-custom-select" 
                                            mode="tags"
                                            v-model="form.user_define_barcode"
                                            placeholder="User Define Barcode"
                                            :searchable="true"
                                            :createTag="true"
                                            :options="user_define_barcode"  
                                            :close-on-select="true" 
                                            :create-option="true"  
                                        />  
                                    </div>
                                </div>
                                <div class="col-md-3">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="allow_checkout_when_out_of_stock">Allow checkout when out of stock</label>
                                        <select class="form-select-custom" v-model="form.allow_checkout_when_out_of_stock" id="allow_checkout_when_out_of_stock">
                                            <option :value="0">No</option> 
                                            <option :value="1">Yes</option> 
                                        </select>   
                                    </div>
                                </div>
                            </div>

                            <div class="row g-3 mt-1" v-if="form.product_type=='variable'"> 
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label class="form-label-custom">Colors</label>
                                        <Multiselect
                                            class="form-control-custom-select" 
                                            mode="tags"
                                            v-model="form.color"
                                            placeholder="Select Colors"
                                            :searchable="true"
                                            :createTag="false"
                                            :options="colors"  
                                            :close-on-select="true" 
                                            :create-option="false"  
                                        /> 
                                    </div> 
                                </div> 
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label class="form-label-custom">Sizes</label>
                                        <Multiselect
                                            class="form-control-custom-select" 
                                            mode="tags"
                                            v-model="form.size"
                                            placeholder="Select Size"
                                            :searchable="true"
                                            :createTag="false"
                                            :options="sizes"  
                                            :close-on-select="true" 
                                            :create-option="false"  
                                        />  
                                    </div>     
                                </div>
                            </div>
                            
                            <div class="mt-3" v-if="form.product_type=='variable'">
                                <label class="form-label-custom mb-2">Dynamic Attributes</label>
                                <div class="row g-2 mb-2 align-items-center" v-for="(attrGroup, index) in form.selected_attributes" :key="index">
                                    <div class="col-md-4">
                                        <select class="form-select-custom" v-model="attrGroup.attribute_id" @change="attrGroup.values = []">
                                            <option :value="null">Select Attribute</option>
                                            <option :value="attr.value" v-for="(attr, i) in attributes" :key="i">{{attr.label}}</option>
                                        </select>
                                    </div>
                                    <div class="col-md-6">
                                        <Multiselect
                                            class="form-control-custom-select" 
                                            mode="tags"
                                            v-model="attrGroup.values"
                                            placeholder="Select Attribute Values"
                                            :searchable="true"
                                            :createTag="false"
                                            :options="attribute_values.filter(v => v.attribute_id === attrGroup.attribute_id)"  
                                            :close-on-select="true" 
                                            :create-option="false"  
                                            :disabled="!attrGroup.attribute_id"
                                        />
                                    </div>
                                    <div class="col-md-2">
                                        <button type="button" class="btn btn-sm btn-outline-danger" @click="removeAttributeRow(index)">
                                            <i class="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                                <button type="button" class="btn btn-sm btn-outline-primary" @click="addAttributeRow">
                                    <i class="fas fa-plus me-1"></i> Add More Attribute
                                </button>
                            </div>

                            <div class="mt-1" v-if="form.product_type=='combo'">
                                <label class="form-label-custom mb-2 text-indigo fw-bold"><i class="fas fa-layer-group me-1 text-primary"></i> Combo Products Selection</label>
                                
                                <div class="row g-2 mb-1" v-if="form.combo_items.length > 0">
                                    <div class="col-md-4"><label class="form-label-custom text-muted small mb-0">Product</label></div>
                                    <div class="col-md-3"><label class="form-label-custom text-muted small mb-0">Quantity</label></div>
                                    <div class="col-md-3"><label class="form-label-custom text-muted small mb-0">Price</label></div>
                                    <div class="col-md-2"></div>
                                </div>

                                <div class="row g-2 mb-2 align-items-center" v-for="(comboItem, index) in form.combo_items" :key="index">
                                    <div class="col-md-4">
                                        <select class="form-select-custom" v-model="comboItem.product_id">
                                            <option :value="null">Select Product</option>
                                            <option :value="prod.value" v-for="(prod, i) in standard_products" :key="i">{{prod.label}}</option>
                                        </select>
                                    </div>
                                    <div class="col-md-3">
                                        <input type="number" class="form-control-custom" v-model="comboItem.quantity" min="1" placeholder="Quantity">
                                    </div>
                                    <div class="col-md-3">
                                        <input type="number" class="form-control-custom" v-model="comboItem.price" min="0" placeholder="Price">
                                    </div>
                                    <div class="col-md-2">
                                        <button type="button" class="btn btn-sm btn-outline-danger" @click="removeComboRow(index)">
                                            <i class="fas fa-trash"></i>
                                        </button>
                                    </div>
                                </div>
                                <button type="button" class="btn btn-sm btn-outline-primary mt-1" @click="addComboRow">
                                    <i class="fas fa-plus me-1"></i> Add More Product
                                </button>
                            </div>
                        </div>

                        <!-- Group 4: Media & Description -->
                        <div class="card bg-light-custom border-0 p-3 mb-2">
                            <h5 class="text-indigo fw-bold mb-3"><i class="fas fa-image me-2"></i>Media & Descriptions</h5>
                            <div class="row g-3"> 
                                <div class="col-md-6">
                                    <div class="form-group"> 
                                        <label class="form-label-custom">Main / Feature Image <small class="text-muted">(350X350 px)</small></label>
                                        <div class="d-flex align-items-center gap-3">
                                            <div class="file-upload-wrapper">
                                                <button type="button" class="btn btn-outline-file-upload"><i class="fa fa-camera me-2"></i>Choose Image</button>
                                                <input type="file" class="file-upload-input" name="thumbnail" @change="onImageChange"/>
                                            </div>
                                            <div class="image-avatar-container" style="width: 50px; height: 50px; border-radius: 8px;" v-if="form.thumbnail || thumbnailPreview">
                                                <img :src="thumbnailPreview || form.thumbnail" class="image-avatar">
                                            </div>
                                        </div> 
                                    </div>                                
                                </div> 
                                <div class="col-md-6" v-if="form.is_ecommerce"> 
                                    <div class="form-group">
                                        <label class="form-label-custom">Multiple Images <small class="text-muted">(Same sizes)</small></label>
                                        <div class="d-flex align-items-center gap-3 flex-wrap">
                                            <div class="file-upload-wrapper">
                                                <button type="button" class="btn btn-outline-file-upload"><i class="fa fa-images me-2"></i>Choose Images</button>
                                                <input id="attachments" type="file" class="file-upload-input" multiple="multiple" name="attachments" @change="uploadFieldChange"/>
                                            </div>
                                            <div class="attachment-holder animated fadeIn d-flex gap-2 flex-wrap" v-cloak v-for="(attachment, index) in form.attachments" :key="index"> 
                                                <span class="badge bg-indigo d-flex align-items-center gap-2 py-2 px-3 rounded-3" style="font-size: 11.5px;">
                                                    {{ attachment.name + " (" + Number((attachment.size / 1024 / 1024).toFixed(1)) + "MB)" }}
                                                    <i class="fas fa-times cursor-pointer text-white" @click.prevent="removeAttachment(attachment)"></i>
                                                </span>
                                            </div>
                                        </div>
                                    </div>   
                                </div>   
                            </div>  
                            
                            <div class="row g-3 mt-2">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="short_description">Short Description</label>
                                        <textarea name="short_description" class="form-control-custom" style="height: 100px; resize: none;" placeholder="Enter short description here..."></textarea>  
                                    </div>
                                </div>
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label class="form-label-custom" for="description">Description</label>
                                        <textarea name="description" class="form-control-custom" style="height: 100px; resize: none;" placeholder="Enter description here..."></textarea>  
                                    </div>
                                </div> 
                            </div>  
                        </div> 
                    </div>
                    <div class="modal-footer px-4 py-3 bg-light border-top-0">
                        <button type="submit" class="btn btn-premium px-4" :disabled="disabled">
                            <span v-show="isSubmit"><i class="fas fa-spinner fa-spin me-2"></i></span>
                            {{btn}} 
                        </button>
                    </div>
                </form>
            </div>
        </Modal>

        <Modal @close="toggleModal2" :modalActive="modalShow">
          <div class="modal-content scrollbar-width-thin product-show-modal">
            <div class="modal-header"> 
                <h5 class="">Product Info</h5>
                <button @click="toggleModal2" type="button" class="btn btn-default">X</button>
            </div>
             <div class="modal-body" v-if="product">  
              <div class="row">
                <div class="col-12">
                    <div class="card">
                        <div class="card-body">
                            <div class="row">
                                <div class="col-lg-5">    
                                  <vue-barcode 
                                    :value="product.pro_code" 
                                    :options="{ 
                                      lineColor: '#000', 
                                      displayValue:true,
                                      text:product.product_name,  
                                      flat: true,
                                      format: EAN8,
                                      width: 1,
                                      height: 30 }" >
                                  </vue-barcode> 
                                </div> <!-- end col -->
                                <div class="col-lg-7"> 
                                        <!-- Product title -->
                                        
                                        <div class="col-md-12">
                                          <h3 class="mt-0">{{product.product_name}}  </h3>  
                                          <table class="table table-striped">
                                            <tbody>
                                            <tr>
                                              <td scope="col">Cost Price</td>
                                              <td scope="col">Depo Price</td>
                                              <td scope="col">MRP Price</td>
                                            </tr>
                                            <tr>
                                              <td>{{product.cost_price}}</td>
                                              <td>{{product.depo_price}}</td>
                                              <td>{{product.mrp_price}}</td>
                                            </tr>
                                            </tbody>
                                          </table>
                                        </div>  

                                        <!-- Product information -->
                                        <div class="mt-4">
                                            <div class="row">
                                                <div class="col-md-4">
                                                    <h6 class="font-14">Available Stock:</h6>
                                                    <p class="text-sm lh-150">{{ (product.stock_product) ? product.stock_product[0].stock_quantity : 0}}</p>
                                                </div>
                                                <div class="col-md-4">
                                                    <h6 class="font-14">Sold Stock:</h6>
                                                    <p class="text-sm lh-150">{{ (product.stock_product) ? product.stock_product[0].out_stock_quantity : 0}}</p>
                                                </div>
                                                <!-- <div class="col-md-4">
                                                    <h6 class="font-14">Revenue:</h6>
                                                    <p class="text-sm lh-150">0</p>
                                                </div> -->
                                            </div>
                                        </div> 
                                </div> <!-- end col -->
                            </div> <!-- end row-->
                            <div class="table-responsive mt-4">
                                <table class="table table-bordered table-centered mb-0"> 
                                    <tbody>
                                        <tr>
                                            <td>Product type</td>
                                            <td>{{product.product_type}}</td> 
                                        </tr> 
                                        <tr v-if="product.category_name">
                                            <td>category_name</td>
                                            <td>{{product.category_name.name}}</td> 
                                        </tr>
                                        <tr v-if="product.sub_category_name">
                                            <td>Sub Category</td>
                                            <td>{{product.sub_category_name.name}}</td> 
                                        </tr>
                                        <tr v-if="product.brand_name">
                                            <td>Brand</td>
                                            <td>{{product.brand_name.name}}</td> 
                                        </tr>
                                        <tr v-if="product.is_ecommerce">
                                            <td>is ecommerce</td>
                                            <td>
                                              <span class="badge bg-info" v-if="product.is_ecommerce">Yes</span> 
                                              <span class="badge bg-info" v-else>No</span> 
                                            </td>
                                        </tr> 
                                        <tr v-if="product.is_expirable">
                                            <td>is ecommerce</td>
                                            <td>
                                              <span class="badge bg-info" v-if="product.is_expirable">Yes</span> 
                                              <span class="badge bg-info" v-else>No</span> 
                                            </td>
                                        </tr> 
                                        <tr v-if="product.is_expirable">
                                            <td>is expirable</td>
                                            <td>
                                              <span class="badge bg-info" v-if="product.is_expirable">Yes</span> 
                                              <span class="badge bg-info" v-else>No</span> 
                                            </td>
                                        </tr> 
                                        <tr>
                                            <td>purchase_measuring_unit</td>
                                            <td>
                                              <span class="badge bg-info" v-if="product.is_expirable">Yes</span> 
                                              <span class="badge bg-info" v-else>No</span> 
                                            </td>
                                        </tr> 
                                      </tbody>
                                    </table>
                                  </div> 
                            <div class="col-md-12"> 
                              <div class="form-row">
                                <div class="form-group col-md-3">
                                    
                                </div>
                              </div>
                            </div>
                        </div> <!-- end card-body-->
                    </div> <!-- end card-->
                </div> <!-- end col-->  
              </div>
            </div>
          </div>
        </Modal>

        <Modal @close="masterDataUploadModal" :modalActive="masterModal">
          <div class="modal-content scrollbar-width-thin product-upload-modal ">
            <div class="modal-header"> 
                <h3>Master Data Uploads</h3>
                <button @click="masterDataUploadModal" type="button" class="btn btn-default">X</button>
            </div>
             <div class="modal-body">  
               <div class="row">
                <div class="col-md-12">
                  <form @submit.prevent="uploadMasterDataExcel()" role="form" enctype="multipart/form-data">
                      <div class="row">
                        <div class="form-group">   
                          <div class="mb-3">
                          <a :href="samplefile_master_data_url"  @click="downloadSampleMasterDataFile" class="btn btn-info" download><i class="fa fa-download" aria-hidden="true" ></i> Download Sample file</a>
                        </div>
                        </div> 
                      </div> 
                      <div class="row">
                        <div class="form-group">  
                          <span class="btn btn-primary btn-file">
                            <span class="fileinput-new" id="master-data-excel-file">
                              <i class="fas fa-file-excel"></i> Choose Excel
                            </span>
                            <input type="file" id="maste_file" ref="maste_file" class="form-control upload-file" @change="handleFileMasterDataUpload()"/>
                          </span>
                          <button type="submit" class="btn btn-info float-right"><i class="fa fa-upload" aria-hidden="true"></i> {{ button_name }}</button>
                        </div>
                      </div>
                  </form>
              </div>
            </div>
            </div>
          </div>
        </Modal>

        <Modal @close="toggleImportModal" :modalActive="importModal">
          <div class="modal-content scrollbar-width-thin product-upload-modal ">
            <div class="modal-header"> 
                <h3>Import Products</h3>
                <button @click="toggleImportModal" type="button" class="btn btn-default">X</button>
            </div>
             <div class="modal-body">  
               <div class="row">
                <div class="col-md-12">
                  <form @submit.prevent="uploadExcel()" role="form" enctype="multipart/form-data">
                      <div class="row">
                        <div class="form-group">   
                          <div class="mb-3">
                          <a :href="samplefile_url"  @click="downloadSampleFile" class="btn btn-info" download><i class="fa fa-download" aria-hidden="true" ></i> Download Sample file</a>
                        </div>
                        </div> 
                      </div> 
                      <div class="row">
                        <div class="form-group">  
                          <span class="btn btn-primary btn-file">
                            <span class="fileinput-new" id="excel-file">
                              <i class="fas fa-file-excel"></i> Choose Excel
                            </span>
                            <input type="file" id="file" ref="file" class="form-control upload-file" @change="handleFileUpload()"/>
                          </span>
                          <button type="submit" class="btn btn-info float-right"><i class="fa fa-upload" aria-hidden="true"></i> {{ button_name }}</button>
                        </div>
                      </div>
                  </form>
              </div>
            </div>
            </div>
          </div>
        </Modal>
      </div>
    </transition>
</template>
<script> 
import Modal from "./../helper/Modal"; 
import { ref } from "vue";
import Form from 'vform'  
import Vue3Barcode from 'vue3-barcode'
import axios from 'axios'; 
import Bulkupload from './Bulkupload'; 
import Datatable from '@/components/Datatable.vue';
import Pagination from '@/components/Pagination.vue';
 
import BarcodeGenerator from "@/components/BarcodeGenerator.vue"; 
import BarcodeDemo from "@/views/product/BarcodeDemo"; 

export default {
    name: 'Products',
    components: { 
      Modal,
      Vue3Barcode, 
      Bulkupload:Bulkupload,
      Datatable,
      Pagination, 
      BarcodeGenerator,
      BarcodeDemo
    },
    props:{
        language: {
          type: Object,
          default: () => {
            return {
              lengthMenu: null,
              info: null,
              zeroRecords: null, 
              search: null
            }
          },
        },
    },
    data() {
        return {
            editor: null,
            barcodeOprions:{ lineColor: '#0000FF' },
            isSubmit: false,
            showModal: false,
            editMode:false,
            disabled: false, 
            loading: true, 
            errors: {},
            logoPreview:'',
            btn:'Submit',
            isShow: false,
            showModal: false, 
            errors: {},
            items: [],
            product:'',
            parent_id:0,
            parents:[],
            childs:[],
            brands:[], 
            units:[], 
            sizes:[],
            colors:[],
            attributes:[],
            attribute_values:[],
            standard_products:[],
            products:[],
            isValid: true,
            regex: '/[0-9]/',
            mynumber: "",
            suppliers:[], 
            thumbnailPreview:'',
            attachmentsPreview:false,             
            producttaxs:[{id: 1, name: 'Exclusive'}, {id: 2, name: 'Inclusive'}], 
            producttax:[{id: 1, title:'No tax'}], 
            value: null,
            user_define_barcode: [],
            product_tags: [],
            form: new Form({
                id:'',
                product_type:'standard',
                product_name:'',
                product_native_name:'',
                product_code:'',
                category_id:'',
                sub_category_id:'', 
                brand_id:'',
                barcode_symbology:'', 
                min_order_qty: "1",
                cost_price:'',
                depo_price:'',
                mrp_price:'', 
                tax_method:'',
                discount:'',
                product_tax:'',
                alert_quantity:0,
                product_tags:[],
                thumbnail:'',    
                supplier_id: [], 
                size: [], 
                color: [], 
                selected_attributes: [{ attribute_id: null, values: [] }],
                combo_items: [{ product_id: null, quantity: 1, price: 0 }],
                attachments: [],
                short_description: "", 
                description: "",
                status: 1,   
                is_expirable:0,
                is_ecommerce:0,
                user_define_barcode:[],
                purchase_measuring_unit:'',
                sales_measuring_unit:'',
                convertion_rate:'',
                carton_size:'',
                carton_c_p_u:'',
                is_outlet_management:'',
                allow_checkout_when_out_of_stock:0
            }),
            file : '',
            maste_file : '',
            attachment : '',
            upload_size : 0, 
            button_name : "Upload",
            validation_error : null,
            multiclasses: { 
              clear: '',
              clearIcon: '', 
            }, 
            columns: [ 
                {
                    label: 'Company Name',
                    name: 'company',           
                    width: '10%'
                },  
                {
                    label: 'Product Name',
                    name: 'product_name',           
                    width: '25%'
                },  
                {
                    label: 'Native Name',
                    name: 'product_native_name',           
                    width: '25%'
                },   
                {
                    label: 'Cost Price',
                    name: 'cost_price',
                    width: '10%'
                },
                {
                    label: 'MRP Price',
                    name: 'mrp_price',
                    width: '10%'
                },
                {
                    label: 'Product Code',
                    name: 'product_code',
                    width: '15%'
                },  
                {
                    label: 'Category Name',
                    name: 'category.name',
                    width: '10%'
                },
                {
                    label: 'Alert Qty',
                    name: 'alert_quantity',
                    width: '10%'
                },
                {
                    label: 'Actions',            
                    name: '',
                    isSearch: false, 
                    isAction: true,
                    width: '5%',

                }
            ],  
            tableData: {
                draw: 0,
                length: 10,
                search: '',
                column: 0,
                dir: 'desc',
                sortKey: 'product_name', 
                category_id:'',
                sub_cat_id:'',
                brand_id:'',
                company_id:''
            }, 
            lang: {
                lengthMenu: this.$props.language.lengthMenu ? this.$props.language.lengthMenu : 'Show_MENU_entries',
                info: this.$props.language.info ? this.$props.language.info : 'Showing_FROM_to_TO_of_TOTAL_entries',
                zeroRecords: this.$props.language.zeroRecords ? this.$props.language.zeroRecords : 'No data available in table.', 
                search: this.$props.language.search ? this.$props.language.search : 'Search'
            },
            pagination: {
                lastPage: '',
                currentPage: '',
                total: '',
                lastPageUrl: '',
                nextPageUrl: '',
                prevPageUrl: '',
                from: '',
                to: '',
                links:[],
            },
            samplefile_url: this.sampleFileUrl+'/import_excel_demo/sample-products.xlsx',
            samplefile_master_data_url: this.sampleFileUrl+'/import_excel_demo/sample-master-data.xlsx',
            filter:{
              sub_cat_id:'',
              category_id:'',
              brand_id:'',
              company_id:'',
            },
            barcodeWidth:1.2,
            barcodeHeight:30,
            barcodeReRender:true,
            singleBarcodeEnable: false,
            barcode_symbology:'CODE128',
            color:'Black',
            barcodeQty:1,
            companies: [],
            barcode_symbology_list:[{name:'CODE128'},{name:'EAN13'}, {name:'UPC'},{name:'CODE39'},{name:'MSI'}, {name:'codabar'},{name:'ITF14'} ]
        };
    },

    setup() {
        const modalActive = ref(false);
        const modalActiveBarcode = ref(false);
        const modalShow = ref(false);
        const importModal = ref(false); 
        const masterModal = ref(false); 
        return { modalActive, modalActiveBarcode, modalShow, importModal, masterModal };
    },
    beforeUnmount() {
      //this.editor.destroy()
    }, 
    methods: {
        toggleModal: function() {
          this.modalActive = !this.modalActive;    
          if(!this.modalActive){
            this.editMode = false;
            this.btn='Create';
          } 
          this.errors = '';
          this.isSubmit = false;
          this.form.reset();  
        }, 
        toggleModal2: function() {  
          this.modalShow = !this.modalShow;
        },
        toggleBarcode: function() {
          this.singleBarcodeEnable = false;
          this.modalActiveBarcode = !this.modalActiveBarcode;
        },
        toggleImportModal: function() {  
          this.importModal = !this.importModal;
        },
        masterDataUploadModal: function() {  
          this.masterModal = !this.masterModal;
        }, 
        singleBarcode : function(show){
          this.modalActiveBarcode = !this.modalActiveBarcode;
          this.singleBarcodeEnable = true;
          this.product = show;
          this.product.barcode_symbology = this.barcode_symbology;
          this.barcodeQty = this.product.quantity ? this.product.quantity : 1 ;
          console.log(show) 
        },
        forceRerender() {
          this.barcodeReRender = false;
          this.$nextTick(() => { 
              this.barcodeReRender = true;
          });
        },
        barcodeSetting: function(){ 
          this.forceRerender();
        },
        selectProduct(event) {   
          this.forceRerender();
          this.singleBarcodeEnable = true;
          this.product = this.items.find(e => e.id == event);    
          console.log('this.product', this.product)
        },
        print() { 
          this.$htmlToPaper("barcodePrint");
        }, 
        edit: function(item) {  
            axios.get(this.apiUrl+'/products/'+item.id, this.headerjson)
            .then((res) => {
              let getItem = res.data.data;  
              this.fetchChildsCat(item.category_id); 
              getItem.user_define_barcode.forEach(fields => {                
                if(getItem.product_code != fields){ 
                  this.user_define_barcode.push(fields);
                }
              }); 
              getItem.product_tags.forEach(fields => {   
                this.product_tags.push(fields);
              });
              this.form.fill(res.data.data);
              
              // Reconstruct dynamic attributes
              if(getItem.attribute_values && getItem.attribute_values.length > 0) {
                  let attrGroups = {};
                  getItem.attribute_values.forEach(valId => {
                      let found = this.attribute_values.find(v => v.value === valId);
                      if(found) {
                          if(!attrGroups[found.attribute_id]) attrGroups[found.attribute_id] = [];
                          attrGroups[found.attribute_id].push(valId);
                      }
                  });
                  this.form.selected_attributes = Object.keys(attrGroups).map(key => ({
                      attribute_id: parseInt(key),
                      values: attrGroups[key]
                  }));
              }
              if(!this.form.selected_attributes || this.form.selected_attributes.length === 0) {
                  this.form.selected_attributes = [{ attribute_id: null, values: [] }];
              }
              
              if(getItem.combo_items && getItem.combo_items.length > 0) {
                  this.form.combo_items = getItem.combo_items;
              } else {
                  this.form.combo_items = [{ product_id: null, quantity: 1, price: 0 }];
              }
            });  
            this.btn='Update';
            this.editMode = true;
            this.thumbnailPreview='';
            this.attachmentsPreview = false;
            this.toggleModal(); 
        },
        show: function(item) {     
          axios.get(this.apiUrl+'/products/'+item.id, this.headers).then(res => {
              if(res.status == 200){  
                this.product = res.data.data
                this.modalShow = !this.modalShow;  
              }else{
                this.$toast.error(res.data.message);
              }
              console.log(res.data.data)
          }).catch(err => {  
              this.$toast.error(err.response.data.message);
              if(err.response.status == 422){
                  this.errors = err.response.data.errors 
              }
          }) 
        }, 
        submitForm: function(e) { 
            this.isSubmit = true;
            this.disabled = true;
            const formData = new FormData();  
            formData.append('product_type', this.form.product_type);            
            formData.append('product_name', this.form.product_name);
            formData.append('product_native_name', this.form.product_native_name);
            formData.append('product_code', this.form.product_code);
            formData.append('category_id', this.form.category_id);
            formData.append('sub_category_id', this.form.sub_category_id);
            formData.append('brand_id', this.form.brand_id);
            formData.append('barcode_symbology', this.form.barcode_symbology); 
            formData.append('min_order_qty', this.form.min_order_qty); 
            formData.append('cost_price', this.form.cost_price); 
            formData.append('depo_price', this.form.depo_price); 
            formData.append('mrp_price', this.form.mrp_price); 
            formData.append('tax_method', this.form.tax_method); 
            formData.append('product_tax', this.form.product_tax); 
            formData.append('discount', this.form.discount); 
            formData.append('alert_quantity', this.form.alert_quantity); 
            formData.append('product_tags', this.form.product_tags);  
            formData.append('supplier_id', this.form.supplier_id); 
            formData.append('size', this.form.size); 
            formData.append('color', this.form.color);  
            
            let flatAttributes = [];
            if(this.form.selected_attributes) {
                this.form.selected_attributes.forEach(attr => {
                    if(attr.values && attr.values.length > 0) {
                        flatAttributes = flatAttributes.concat(attr.values);
                    }
                });
            }
            formData.append('attribute_values', flatAttributes);
            formData.append('combo_items', JSON.stringify(this.form.combo_items));
            formData.append('short_description', this.form.short_description); 
            formData.append('description', this.form.description); 
            formData.append('status', this.form.status); 
            formData.append('is_expirable', this.form.is_expirable); 
            formData.append('is_ecommerce', this.form.is_ecommerce);  
            formData.append('purchase_measuring_unit', this.form.purchase_measuring_unit); 
            formData.append('sales_measuring_unit', this.form.sales_measuring_unit); 
            formData.append('convertion_rate', this.form.convertion_rate); 
            formData.append('carton_size', this.form.carton_size); 
            formData.append('carton_c_p_u', this.form.carton_c_p_u); 
            formData.append('is_outlet_management', this.form.is_outlet_management);  
            formData.append('allow_checkout_when_out_of_stock', this.form.allow_checkout_when_out_of_stock);   
            if(this.form.user_define_barcode.length >0){
              for (var x = 0; x < this.form.user_define_barcode.length; x++) {
                if(this.form.product_code !=this.form.user_define_barcode[x]){
                  formData.append('user_define_barcode[]', this.form.user_define_barcode[x]);
                } 
              }
            } 
            if(this.editMode){
                formData.append('_method', 'put');
                if(this.thumbnailPreview){
                  this.form.thumbnail ? formData.append('thumbnail', this.form.thumbnail, this.form.thumbnail.name) : '';
                } 
                if(this.attachmentsPreview){
                  for (var x = 0; x < this.form.attachments.length; x++) { 
                    formData.append('attachments[]', this.form.attachments[x], this.form.attachments[x].name);
                  }
                }
                var postEvent = axios.post(this.apiUrl+'/products/'+this.form.id, formData, this.headers);
                
            }else{ 
                if(this.attachmentsPreview){
                  for (var x = 0; x < this.form.attachments.length; x++) { 
                    formData.append('attachments[]', this.form.attachments[x], this.form.attachments[x].name);
                  }
                }
                this.form.thumbnail ? formData.append('thumbnail', this.form.thumbnail, this.form.thumbnail.name) : '';
                var postEvent = axios.post(this.apiUrl+'/products', formData, this.headers);
            }
            postEvent.then(res => {
                this.isSubmit = false;
                this.disabled = false;
                if(res.status == 200){ 
                  this.toggleModal();
                  this.$toast.success(res.data.message); 
                  //window.location.reload();
                }else{
                  this.$toast.error(res.data.message);
                } 
            }).catch(err => { 
                this.isSubmit = false; 
                this.disabled = false; 
                this.$toast.error(err.response.data.message);
                if(err.response.status == 422){
                    this.errors = err.response.data.errors ; 
                }
                
            }).finally((  ) => { 
              this.fetchItems(); 
            }); 
        },
        onkeyPress: function(field) { 
            for (var k in this.errors){     // Loop through the object
                if(k==field){      // If the current key contains the string we're looking for 
                  console.log('delete', this.errors[k])
                  delete this.errors[k];  // Delete obj[key];
                }
            }  
        }, 
        validateNumber:function(e,field){ 
          const number = e.target.value;
          if(this.isNumber(number)){
            this.form[field] = number;
            this.onkeyPress(this.form[field]);
          }else{
            this.form[field] = '';
          }
        }, 
        isNumber: function(value) {
            if ((undefined === value) || (null === value)) {
                return false;
            }
            if (typeof value == 'number') {
                return true;
            }
            return !isNaN(value - 0);
        },
        onImageChange(e) {
            this.form.thumbnail = e.target.files[0]
            let files = e.target.files || e.dataTransfer.files;
            if (!files.length)
                return; 
            this.createImage(files[0]);
        },
        createImage(file) {
            let reader = new FileReader();
            let vm = this;
            reader.onload = (e) => { 
                vm.thumbnailPreview = e.target.result;
            };
            reader.readAsDataURL(file);
        },
        getAttachmentSize() {
          this.upload_size = 0; // Reset to beginningƒ
          this.form.attachments.map((item) => {
            this.upload_size += parseInt(item.size);
          }); 
          this.upload_size = Number(this.upload_size.toFixed(1)); 
        },
        removeAttachment(attachment) {
          this.form.attachments.splice(
            this.form.attachments.indexOf(attachment),
            1
          ); 
          this.getAttachmentSize();
        },
        uploadFieldChange(e) { 
          this.attachmentsPreview = true;
          var files = e.target.files || e.dataTransfer.files; 
          if (!files.length) return;
          for (var i = files.length - 1; i >= 0; i--) {
            this.form.attachments.push(files[i]);
          } 
          document.getElementById("attachments").value = [];
        },
        
        switchSelect(event){   
          this.fetchChildsCat(event)
        },
        addAttributeRow() {
            this.form.selected_attributes.push({ attribute_id: null, values: [] });
        },
        removeAttributeRow(index) {
            this.form.selected_attributes.splice(index, 1);
            if(this.form.selected_attributes.length === 0) {
                this.form.selected_attributes.push({ attribute_id: null, values: [] });
            }
        },
        addComboRow() {
            this.form.combo_items.push({ product_id: null, quantity: 1, price: 0 });
        },
        removeComboRow(index) {
            this.form.combo_items.splice(index, 1);
            if(this.form.combo_items.length === 0) {
                this.form.combo_items.push({ product_id: null, quantity: 1, price: 0 });
            }
        },
        random_num(){
          const d1 = new Date();
          const result = d1.getTime(); 
          this.form.product_code = result; 
          this.onkeyPress('product_code')
        }, 
        async fetchHelpers() {
            await axios.get(this.apiUrl+'/products-helper',this.headers)
            .then((res) => {
                this.parents = res.data.data.categories.map(({ id, name }) => ({ label: name, value: id })); 
                this.brands = res.data.data.brands; 
                this.units = res.data.data.units; 
                this.suppliers = res.data.data.suppliers.map(({ id, name }) => ({ label: name, value: id })); 
                this.colors = res.data.data.colors.map(({ id, name }) => ({ label: name, value: id })); 
                this.sizes = res.data.data.sizes.map(({ id, name }) => ({ label: name, value: id }));  
                this.attributes = res.data.data.attributes.map(({ id, title }) => ({ label: title, value: id }));
                this.attribute_values = res.data.data.attribute_values.map(({ id, value, attribute }) => ({ label: value, value: id, attribute_id: attribute.id }));
                this.standard_products = res.data.data.standard_products.map(({ id, product_name }) => ({ label: product_name, value: id }));
                this.producttax = res.data.data.taxes;
            });
        },
        fetchChildsCat(parent_id) {
            this.childs = [];
            axios.get(this.apiUrl+'/product_categories?parent_id='+parent_id, this.headerjson)
            .then((res) => { 
                this.childs = res.data.data.map(({ id, name }) => ({ label: name, value: id })); 
            });
        }, 
        handleFileUpload(){
            this.file = this.$refs.file.files[0];
            document.getElementById("excel-file").innerHTML = '<i class="fas fa-file-excel"></i> ' + this.file.name;
        }, 

        uploadExcel: function( ) {  
            this.button_name = "Uploading...";
            const formData = new FormData();
            formData.append("file", this.file);
                    
            var postEvent = axios.post(this.apiUrl+'/products-import', formData, this.headers);

            postEvent.then(res => {
                if(res.status == 200){
                    this.file = '';
                    this.toggleImportModal();
                    this.button_name = "Upload";
                    this.$toast.error(res.data.data.exists); 
                    this.$toast.error(res.data.data.code); 
                    this.$toast.success(res.data.data.inserted);  
                    // window.location.reload();

                }else{
                    this.$toast.error(res.data.message);
                }

            }).catch(err => { 
                this.file = '';
                this.$refs.file.value=null;
                this.$toast.error(err.response.data.message);
                if(err.response.status == 422){
                    this.errors = err.response.data.errors 
                }

            });
        },

        handleFileMasterDataUpload(){
            this.file = this.$refs.maste_file.files[0];
            console.log('this.file', this.file)
            document.getElementById("master-data-excel-file").innerHTML = '<i class="fas fa-file-excel"></i> ' + this.file.name;
        }, 
        uploadMasterDataExcel(){
            this.button_name = "Uploading...";
            let formData = new FormData();
            formData.append('file', this.file); 
            axios.post(this.apiUrl+'/master-data-upload/',formData,this.headers)
            .then((res) => {
                this.isSubmit = false;
                this.disabled = false;
                if(res.status == 200){  
                  //this.toggleModal();
                  this.$toast.error(res.data.data.exists); 
                  this.$toast.error(res.data.data.code); 
                  this.$toast.success(res.data.data.inserted);  

                }else{
                  this.$toast.error(res.data.message);
                } 
            })
            .catch((err) => { 
              this.isSubmit = false; 
              this.disabled = false; 
              this.$toast.error(err.response.data.message);
              if(err.response.status == 422){
                  this.errors = err.response.data.errors ; 
              } 
            })
            .finally(( ) => {
              this.loading = false; 
            }); 
        },
        // datatable For Pagination 
        fetchItems(url = this.apiUrl+'/product/list') { 
            this.tableData.draw++;
            axios.get(url, {params:this.tableData, headers:this.headerparams})
            .then((response) => {
                let data = response.data.data;   
                if(this.tableData.draw = data.draw) { 
                  this.items = data.data.data;
                  this.products = data.data.data.map(({ id, product_name }) => (
                  { label: product_name, value: id }
                  )); 
                  this.configPagination(data.data);
                }
            })
            .catch(errors => {
                console.log(errors);
            })
            .finally(( ) => {
                this.loading = false;
            });
        },

        configPagination(data){
            this.pagination.lastPage = data.last_page;
            this.pagination.currentPage = data.current_page;
            this.pagination.total   = data.total ? data.total : 0;
            this.pagination.lastPageUrl = data.last_page_url;
            this.pagination.nextPageUrl = data.next_page_url;
            this.pagination.prevPageUrl = data.prev_page_url;
            this.pagination.from = data.from ? data.from : 0;
            this.pagination.to = data.to ? data.to : 0;  
            this.pagination.links = data.links;
        },

        sortBy(key,sortable) {
            this.tableData.sortKey = key;
            //this.sortOrders[key] = this.sortOrders[key] * -1;
            this.tableData.column = this.getIndex(this.columns, 'name', key);
            this.tableData.dir = sortable; ///this.sortOrders[key] === 1 ? 'asc' : 'desc'; 
            this.fetchItems();
        },
        setPage(data){  
            this.fetchItems(data.url); 
        },
        getIndex(array, key, value) {
            return array.findIndex(i => i[key] == value)
        },

        filterData(){
          this.tableData.category_id = this.filter.category_id ? this.filter.category_id : '' ;
          this.tableData.sub_cat_id = this.filter.sub_cat_id ? this.filter.sub_cat_id : '';  
          this.tableData.brand_id = this.filter.brand_id ?  this.filter.brand_id : ''; 
          this.tableData.company_id = this.filter.company_id ? this.filter.company_id : '';
          this.fetchItems(); 
        },
        deleteItem: function(item) {  
            this.$swal({
                title: 'Are you sure?',
                text: "You want delete this item!", 
                showCancelButton: true,
                confirmButtonCategory: '#3085d6',
                cancelButtonCategory: '#d33',
                confirmButtonText: 'Yes, delete it!'
            }).then((result) => { 
                if (result.value) { 
                    axios.delete(this.apiUrl+'/products/'+item.id, this.headers).then(res => {
                        if(res.status == 200){ 
                          this.fetchItems();
                          this.$toast.success(res.data.message); 
                        }else{
                          this.$toast.error(res.data.message);
                        } 
                    }).catch(err => {  
                        this.$toast.error(err.response.data.message);
                        if(err.response.status == 422){
                            this.errors = err.response.data.errors 
                        }
                    }) 
                }else{
                    // this.$toast.error(`Hey! I'm here`);
                    // this.$toast.warning(`Hey! I'm here`);
                    // this.$toast.info(`Hey! I'm here`)
                }
            }); 
        },
        downloadSampleFile(){ 
          this.samplefile_url; 
        },
        downloadSampleMasterDataFile(){ 
          this.samplefile_master_data_url; 
        },
        fetchCompanies() {   
            axios.get(this.apiUrl+'/companies', this.headerjson)
            .then((res) => { 
                this.companies = res.data.data; 
            }).catch((err) => { 
                this.$toast.error(err.response.data.message);
            });
        }
        // datatable For Pagination 
    },
    async created() {    
      await this.fetchItems(); 
      await this.fetchHelpers(); 
      await this.fetchCompanies();       
    },
    destroyed() {},
    mounted() {
        window.scrollTo(0, 0);
    },
    computed: {
      permission() {
            let pname = this.$route.meta.parent_module;
            let module_name = this.$route.meta.module_name;
            let path_name = this.$route.path; 
            let data = '';
            if(this.$route.meta.parent_module){
                data = this.$store.getters.userAllPermissions[pname][0].children[path_name]
            }else{
                data = this.$store.getters.userAllPermissions[module_name][0].other_actions; 
            } 
            return data;
        },
    }
};
</script>
<style scoped>
.page-content {
    background-color: #f8fafc;
    min-height: 85vh;
    padding: 0 12px 12px !important;
}

.page-title {
    margin-top: 2px !important;
    margin-bottom: 2px !important;
    font-size: 1.35rem !important;
}

.page-header-row {
    margin-top: 0 !important;
    margin-bottom: 8px !important;
}

.page-header-row .btn-premium,
.page-header-row .btn-outline-premium,
.page-header-row .btn-outline-success-premium {
    padding: 8px 14px !important;
    font-size: 13px !important;
    height: 36px !important;
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
}

.breadcrumb-item + .breadcrumb-item::before {
    content: ">" !important;
    font-size: 16px;
    vertical-align: middle;
}

.breadcrumb-item a {
    text-decoration: none;
    transition: color 0.2s ease;
}

.breadcrumb-item a:hover,
.text-indigo {
    color: #4f46e5 !important;
}

.text-slate-800 {
    color: #1e293b !important;
}

.btn-premium {
    background: linear-gradient(135deg, #4f46e5 0%, #3730a3 100%);
    color: #ffffff;
    border: none;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.25s ease;
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.25);
}

.btn-premium:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(79, 70, 229, 0.35);
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
    color: #ffffff;
}

.btn-outline-premium {
    background: #ffffff;
    color: #4f46e5;
    border: 1.5px solid #c7d2fe;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.25s ease;
}

.btn-outline-premium:hover {
    background-color: #eef2ff;
    color: #3730a3;
}

.btn-outline-success-premium {
    background: #ffffff;
    color: #0f766e;
    border: 1.5px solid #99f6e4;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.25s ease;
}

.btn-outline-success-premium:hover {
    background-color: #ccfbf1;
    color: #115e59;
}

.filter-card {
    margin-bottom: 8px !important;
    border: 1px solid rgba(0, 0, 0, 0.03);
    border-radius: 12px;
    background-color: #ffffff;
}

.filter-card .card-body {
    padding: 10px 12px !important;
}

.form-label-custom {
    display: block;
    float: none;
    margin: 0;
    font-size: 13px;
    color: #334155;
    font-weight: 600;
}

.form-select-custom,
.form-control-custom {
    width: 100%;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    height: 38px !important;
    padding: 7px 12px !important;
    color: #334155;
    font-size: 13px !important;
    background-color: #ffffff;
    outline: none;
    transition: all 0.2s ease;
}

.form-select-custom:focus,
.form-control-custom:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.length-select {
    width: 76px;
}

.search-container {
    width: 320px;
    max-width: 100%;
}

.search-icon {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 5;
}

.search-input {
    padding-right: 36px !important;
    padding-left: 16px !important;
}

.table-card {
    border: 1px solid rgba(0, 0, 0, 0.03);
    border-radius: 12px;
    background-color: #ffffff;
    overflow: hidden;
}

.table-responsive-custom {
    overflow-x: auto;
}

.table-premium {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    vertical-align: middle;
}

.table-premium th {
    background-color: #f8fafc;
    color: #64748b;
    font-weight: 700;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 10px;
    border-bottom: 1.5px solid #e2e8f0;
    user-select: none;
    white-space: nowrap;
}

.table-premium td {
    padding: 10px;
    color: #334155;
    font-size: 14px;
    border-bottom: 1px solid #f1f5f9;
    background-color: #ffffff;
    transition: background-color 0.2s ease;
    vertical-align: middle;
}

.table-premium tbody tr:hover td {
    background-color: #f8fafc;
}

.table-premium tbody tr:last-child td {
    border-bottom: none;
}

.badge-code {
    background-color: #eef2ff;
    color: #4338ca;
    font-weight: 700;
    font-size: 12px;
    border-radius: 6px;
    padding: 4px 8px;
}

.btn-icon {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: #64748b;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
}

.btn-icon:hover {
    background-color: #f1f5f9;
    color: #334155;
}

.dropdown-premium {
    border-radius: 10px;
    padding: 6px;
    min-width: 170px;
}

.dropdown-premium .dropdown-item {
    border-radius: 6px;
    padding: 8px 12px;
    font-size: 13.5px;
    font-weight: 550;
    transition: all 0.25s ease;
    display: flex;
    align-items: center;
}

.dropdown-premium .dropdown-item:hover {
    background-color: #f8fafc;
}

.dropdown-premium .dropdown-item.text-primary:hover,
.dropdown-premium .dropdown-item.text-info:hover {
    background-color: #eef2ff;
    color: #4f46e5 !important;
    opacity: 0.9;
}

.dropdown-premium .dropdown-item.text-warning:hover {
    background-color: #fffbeb;
    color: #d97706 !important;
}

.dropdown-premium .dropdown-item.text-danger:hover {
    background-color: #fef2f2;
    color: #ef4444 !important;
}

.spinner-premium {
    width: 40px;
    height: 40px;
    border: 3.5px solid #f1f5f9;
    border-top: 3.5px solid #4f46e5;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    display: inline-block;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

:deep(.multiselect) {
    border: 1px solid #cbd5e1 !important;
    border-radius: 8px !important;
    height: 38px !important;
    min-height: 38px !important;
    background-color: #ffffff !important;
    transition: all 0.2s ease !important;
    box-shadow: none !important;
    display: flex;
    align-items: center;
}

:deep(.multiselect.is-active) {
    border-color: #6366f1 !important;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15) !important;
}

:deep(.multiselect-placeholder),
:deep(.multiselect-single-label) {
    color: #64748b !important;
    font-size: 13px !important;
    padding-left: 8px !important;
}

:deep(.multiselect-caret) {
    background-color: #64748b !important;
    margin-right: 8px !important;
}

:deep(.multiselect-dropdown) {
    border-radius: 8px !important;
    border: 1px solid #cbd5e1 !important;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}

:deep(.multiselect-option.is-selected) {
    background: #4f46e5 !important;
}

:deep(.multiselect-option.is-pointed) {
    background: #f1f5f9 !important;
    color: #1e293b !important;
}

@media (max-width: 767.98px) {
    .search-container {
        width: 100%;
    }

    .page-header-row .btn-premium,
    .page-header-row .btn-outline-premium,
    .page-header-row .btn-outline-success-premium {
        width: 100%;
        justify-content: center;
    }
}

.btn-file {
  overflow: hidden;
  position: relative;
  vertical-align: middle;
}
 
label {
    display: inline-block;
    margin: 0px 0px 4px 2px;
    float: left;
}

.invalid-feedback {
    display: block;
    width: 100%;
    margin-top: 0.25rem;
    font-size: 80%;
    color: #dc3545;
    text-align: left;
}
.center {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
}

  .multiselect-tag.is-user {
    padding: 5px 8px;
    border-radius: 22px;
    background: #35495e;
    margin: 3px 3px 8px;
  }

  .multiselect-tag.is-user img {
    width: 18px;
    border-radius: 50%;
    height: 18px;
    margin-right: 8px;
    border: 2px solid #ffffffbf;
  }

  .multiselect-tag.is-user i:before {
    color: #ffffff;
    border-radius: 50%;;
  }

  .user-image {
    margin: 0 6px 0 0;
    border-radius: 50%;
    height: 22px;
  }
  .multiselect-clear { 
    display: inline-block !important;
    float: right !important;;
  }
  .multiselect { 
    display: block;
    position: relative; 
  }
  .multiselect.is-active{
    z-index: 1;
  }

  .modal-content.scrollbar-width-thin.product-add-modal {
    width: 95% !important;
    margin: 0px auto;
  }
</style>
<style src="@vueform/multiselect/themes/default.css"></style>